// v3.4: Admin-only chronological bonus ledger for one client.
// Fetches /api/admin/bonuses/by-client/{id} and renders rows with
// running totals. Positives green, chargebacks red.

import React, { useEffect, useState } from 'react';

function fmtMoney(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return '$0.00';
  const abs = Math.abs(num).toFixed(2);
  return (num < 0 ? '−$' : '$') + abs;
}

function fmtDate(d) {
  if (!d) return '';
  // Already a YYYY-MM-DD string from the backend
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [y, m, dd] = d.slice(0, 10).split('-');
    return `${parseInt(m)}/${parseInt(dd)}/${y}`;
  }
  return String(d);
}

export default function ClientBonusHistoryModal({ client, onClose, darkMode }) {
  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!client?.id) return;
    setLoading(true);
    setError('');
    fetch(`/api/admin/bonuses/by-client/${client.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [client?.id]);

  if (!client) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: panelBg, borderRadius: 8, width: 920, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: `1px solid ${borderColor}` }}>

        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>📜 Client Bonus History</div>
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
              {client.name} · #{client.id}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ color: textSecondary, fontSize: 13 }}>Loading history…</div>}
          {error && <div style={{ color: '#A32D2D', fontSize: 13 }}>{error}</div>}
          {data && (
            <>
              {/* Aggregate totals */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 10, background: bg, borderRadius: 6, border: `1px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entries</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary, marginTop: 4 }}>{data.entry_count}</div>
                </div>
                <div style={{ padding: 10, background: bg, borderRadius: 6, border: `1px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Paid</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#27AE60', marginTop: 4 }}>{fmtMoney(data.total_paid)}</div>
                </div>
                <div style={{ padding: 10, background: bg, borderRadius: 6, border: `1px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Chargebacks</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#A32D2D', marginTop: 4 }}>{fmtMoney(data.total_chargebacks)}</div>
                </div>
                <div style={{ padding: 10, background: bg, borderRadius: 6, border: `2px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Total</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: parseFloat(data.net_total) < 0 ? '#A32D2D' : textPrimary, marginTop: 4 }}>{fmtMoney(data.net_total)}</div>
                </div>
              </div>

              {/* Ledger table */}
              {data.entries.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: textSecondary, fontSize: 13 }}>
                  No bonus history yet for this client.
                </div>
              ) : (
                <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: bg, color: textSecondary, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>Effective</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>Staff</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>Policy #</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>Carrier</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>Net</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>Running</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map(e => {
                        const isCB = e.is_chargeback;
                        const rowColor = isCB ? '#A32D2D' : textPrimary;
                        return (
                          <tr key={e.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <td style={{ padding: '8px 10px', color: rowColor }}>{fmtDate(e.effective_date)}</td>
                            <td style={{ padding: '8px 10px', color: rowColor }}>{e.staff_name}</td>
                            <td style={{ padding: '8px 10px', color: rowColor, fontFamily: 'monospace', fontSize: 11 }}>
                              {e.policy_number_raw}
                              {isCB && <span style={{ marginLeft: 6, padding: '1px 5px', fontSize: 9, fontWeight: 700, background: '#A32D2D', color: '#fff', borderRadius: 3 }}>CB</span>}
                            </td>
                            <td style={{ padding: '8px 10px', color: rowColor }}>{e.carrier || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: rowColor, fontWeight: 600 }}>
                              {fmtMoney(e.net_total_calculated)}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: parseFloat(e.running_total) < 0 ? '#A32D2D' : textPrimary, fontWeight: 600 }}>
                              {fmtMoney(e.running_total)}
                            </td>
                            <td style={{ padding: '8px 10px', color: textSecondary, fontSize: 11 }}>{e.status}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
